"use client";

import { useCallback, useEffect, useRef } from "react";
import { STALL_THRESHOLD_MS } from "./triggers";

export type InputCaptureResult = {
  lastActivityRef: React.MutableRefObject<number>;
};

export function useInputCapture(onStall: () => void, enabled: boolean): InputCaptureResult {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(0);

  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!enabled) return;
    timerRef.current = setTimeout(onStall, STALL_THRESHOLD_MS);
  }, [onStall, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const handleActivity = () => resetTimer();
    document.addEventListener("pointerdown", handleActivity);
    document.addEventListener("keydown", handleActivity);
    resetTimer();
    return () => {
      document.removeEventListener("pointerdown", handleActivity);
      document.removeEventListener("keydown", handleActivity);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, resetTimer]);

  return { lastActivityRef };
}
